import React from 'react';

/**
 * Reusable DateTimeInput Component.
 * Styles with Tailwind CSS, supporting datetime-local type.
 *
 * @param {object} props - Component props.
 * @param {string} [props.id] - ID for the input, also used for label's htmlFor.
 * @param {string} [props.name] - Name attribute for the input.
 * @param {string} [props.label] - Optional label text for the input.
 * @param {string} [props.placeholder=''] - Placeholder text.
 * @param {string} [props.value] - Controlled component value (should be in 'YYYY-MM-DDTHH:mm' format).
 * @param {function} [props.onChange] - Change event handler.
 * @param {boolean} [props.required=false] - If true, input is required.
 * @param {boolean} [props.disabled=false] - If true, input is disabled.
 * @param {string} [props.className=''] - Additional custom Tailwind classes for the input element.
 * @param {string} [props.labelClassName=''] - Additional custom Tailwind classes for the label element.
 * @param {string} [props.containerClassName=''] - Additional custom Tailwind classes for the wrapper div.
 * @param {string} [props.error=''] - Error message to display below the input.
 */
const DateTimeInput = ({
    id,
    name,
    label,
    placeholder = '',
    value,
    onChange,
    required = false,
    disabled = false,
    className = '',
    labelClassName = '',
    containerClassName = '',
    error = '',
    ...props
}) => {
    const inputBaseStyles = 'w-full px-4 py-2 border rounded-lg ' +
        'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gmail-blue ' +
        'transition-all duration-200 ease-in-out ' +
        'placeholder-gray-400 text-text-primary disabled:bg-gray-light disabled:cursor-not-allowed';

    const errorStyles = error ? 'border-gmail-red' : 'border-gray-medium';

    return (
        <div className={`mb-4 ${containerClassName}`}>
            {label && (
                <label htmlFor={id || name} className={`block text-sm font-medium text-text-secondary mb-1 ${labelClassName}`}>
                    {label}
                    {required && <span className="text-gmail-red ml-1">*</span>}
                </label>
            )}
            <input
                type="datetime-local" // Specific type for date and time
                id={id || name}
                name={name}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                required={required}
                disabled={disabled}
                className={`${inputBaseStyles} ${errorStyles} ${className}`}
                {...props}
            />
            {error && <p className="text-gmail-red text-xs mt-1">{error}</p>}
        </div>
    );
};

export default DateTimeInput;
